import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { clientQuery, clientMutation } from "../../../../../../../core/data/GraphQLApi";
import { endpoints } from "../../../../../../../core/defaultValues";
import { resetALL } from "../../../../../../../.globals/redux/actions";
import { ExceptionManager } from "../../../../../../../core/logManager";
import { checkDeviceData } from "../../../../../../../core/data/Helpers";

const INIT_STATE = {
    loading: false,
    data: null,
    error: null,
    event: null,
    open: false,
    unit: null,
    createDeviceReadingExtraField: null,
    loadingCreateDeviceReadingExtraField: false,
    createCommentSuccess: false,
    createCommentError: null,
};

const route = 'modules/driverPerformance/views/events/modals/editEvent/redux/slice';

export const getDriverPerformanceEventVideoDetail = createAsyncThunk('driverPerformance/editEvent/getVideoDetail', async ({ rowId, deviceId, messages, linkedDevice }, { rejectWithValue, getState }) => {
    try {
        const state = getState();
        let event

        const resultEvent = await clientQuery(`
            query ($rowId: String!, $deviceId: String!) {
                data: getDriverPerformanceEvent (rowId: $rowId, deviceId: $deviceId) {
                    rowId
                    eventName
                    eventCode
                    driverId
                    driverName
                    deviceId
                    latitude
                    longitude
                    unitTime
                    unitType
                    deviceData
                    disputed
                    assetId
                    assetName
                    linkedDevice {
                        id
                        esn
                        deviceModelId
                        deviceModelName
                        manufacturerId
                        manufacturerName
                        simProviderName
                        label
                    }
                }
            }
        `,
            {
                rowId: rowId,
                deviceId: deviceId
            },
            endpoints.GRAPHQL_GENERAL_V2
        );
        event = resultEvent?.data
        
        // Get extra fields using searchDeviceReadingExtraFields
        let extraFields = [];
        try {
            const readingId = `${deviceId}-${rowId}`;
            const extraFieldsResult = await clientQuery(`
                query SearchDeviceReadingExtraFields($conditions: [DeviceReadingExtraFieldSearchInput], $sort: [DeviceReadingExtraFieldSortInput], $limit: Int, $offset: Int) {
                    data: searchDeviceReadingExtraFields(conditions: $conditions, sort: $sort, limit: $limit, offset: $offset) {
                        total
                        items {
                            id
                            fieldName
                            jsonValue
                            visibleToDriver
                            createdOn
                            createdBy
                            updatedOn
                            updatedBy
                        }
                    }
                }
            `,
                {
                    "conditions": [
                        {
                            "terms": [readingId],
                            "fields": ["readingId"],
                            "exact": true
                        },
                        {
                            "terms": ["noteDriverCase"],
                            "fields": ["fieldName"],
                            "exact": true
                        }
                    ],
                    "sort": { "field": "createdOn", "order": "DESC" },
                    "limit": 100,
                    "offset": 0
                },
                endpoints.GRAPHQL_GENERAL_V2
            );
            extraFields = extraFieldsResult?.data?.items?.map((item) => {
                return {
                    ...item,
                    jsonValue: JSON.parse(JSON.parse(item.jsonValue))
                };
            }) || [];
        } catch (err) {
            extraFields = [];
        }
        
        if (event) {

            let deviceData = event && checkDeviceData(event.deviceData);
            let videoOptions = {
                mode: "picture",
                id: deviceData?.pictureID,
                pictureId: deviceData?.pictureID,
            };
            if (deviceData.clipID) {
                if (deviceData.duration) {
                    videoOptions.mode = "video";
                }
                videoOptions.id = deviceData?.clipID;
                videoOptions.pictureId = null;
            }
            if (deviceData.url) {
                videoOptions.url = deviceData?.url;
            }
            
            // Use linkedDevice from response if available, otherwise use the one passed as parameter
            const deviceInfo = event.linkedDevice || linkedDevice;
            
            videoOptions = {
                ...videoOptions,
                messages: messages,
                type: "playback",
                autoplay: true,
                thumbnailUrl: deviceData.duration === 0
                    ? deviceData.url
                    : !deviceData?.clipID && !deviceData?.thumbnailUrl
                        ? `/assets/images/clipRequestFailed.png`
                        : deviceData?.thumbnailUrl,
                deviceModel: deviceInfo?.deviceModelName === "360" ? "360" : "4K",
            };

            let data = {
                eventName: event?.eventName,
                eventCode: event?.eventCode,
                latitude: event?.latitude,
                longitude: event?.longitude,
                heading: event?.heading,
                unitType: event?.unitType,
                unitTime: event?.unitTime,
                label: deviceInfo?.label,
                driverName: event?.driverName,
                deviceData: event,
                type: deviceInfo?.manufacturerName,
                videoOptions,
                assetName: event?.assetName,
                assetId: event?.assetId,
                formatDeviceData: deviceData,
                deviceId: event?.deviceId,
                rowId: event?.rowId,
                extraFields: extraFields
            }

            return data
        }
    } catch (exc) {
        ExceptionManager(exc, route, 'getDriverPerformanceEventVideoDetail');
        return rejectWithValue(exc);
    }
});

export const createDeviceReadingExtraField = createAsyncThunk(
    "driverPerformance/editEvent/createDeviceReadingExtraField",
    async (payload, { rejectWithValue, getState }) => {
        try {
            const response = await clientMutation(
                `
                mutation createDeviceReadingExtraField ($rowId: String!, $deviceId: String!, $fieldName: String!, $jsonValue: String!, $visibleToDriver: Boolean) {
                    data: createDeviceReadingExtraField (rowId: $rowId, deviceId: $deviceId, fieldName: $fieldName, jsonValue: $jsonValue, visibleToDriver: $visibleToDriver){
                        id
                        readingId
                        fieldName
                        jsonValue
                        visibleToDriver
                        createdOn
                        createdBy
                    }
                }`,
                {
                    ...payload,
                    jsonValue: JSON.stringify(payload.jsonValue)
                },
                endpoints.GRAPHQL_GENERAL_V2
            );

            return response.data;
        } catch (exc) {
            ExceptionManager(exc, route, 'createDeviceReadingExtraField');
            return rejectWithValue({ error: true, message: exc.message });
        }
    }
);

const driverPerformanceEditEventVideoRedux = createSlice({
    name: 'driverPerformance/editEvent/video',
    initialState: INIT_STATE,
    reducers: {
        reset(state, action) {
            return action.payload ? { ...state, ...action.payload } : { ...INIT_STATE };
        },
        openModalEditEvent(state, action) {
            state.open = true;
            state.event = action.payload.event;
        },
        resetCreateDeviceReadingExtraField(state) {
            state.createDeviceReadingExtraField = null;
            state.loadingCreateDeviceReadingExtraField = false;
            state.createCommentSuccess = false;
            state.createCommentError = null;
        },
        updateEventExtraFieldsLocally(state, action) {
            const { newExtraField } = action.payload;
            
            if (!state.data) return;
            
            // Initialize extraFields if not present
            if (!state.data.extraFields) {
                state.data.extraFields = [];
            }
            
            // extraFields is already an array
            let extraFields = [...state.data.extraFields];
            
            // Add new comment to extraFields with parsed jsonValue
            let processedField = { ...newExtraField };
            
            // Parse the jsonValue twice like the query does
            if (processedField.jsonValue && typeof processedField.jsonValue === 'string') {
                try {
                    processedField.jsonValue = JSON.parse(JSON.parse(processedField.jsonValue));
                } catch (e) {
                }
            }
            
            extraFields.unshift(processedField); // Add to beginning to show newest first
            
            // Update state with new extraFields array
            state.data.extraFields = extraFields;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(resetALL, () => INIT_STATE);

        builder.addCase(getDriverPerformanceEventVideoDetail.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(getDriverPerformanceEventVideoDetail.fulfilled, (state, action) => {
            state.loading = false;
            state.data = action.payload;
        });
        builder.addCase(getDriverPerformanceEventVideoDetail.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        builder.addCase(createDeviceReadingExtraField.pending, (state) => {
            state.loadingCreateDeviceReadingExtraField = true;
            state.createCommentSuccess = false;
            state.createCommentError = null;
        });
        builder.addCase(createDeviceReadingExtraField.fulfilled, (state, action) => {
            state.loadingCreateDeviceReadingExtraField = false;
            state.createDeviceReadingExtraField = action.payload;
            state.createCommentSuccess = true;
            state.createCommentError = null;
        });
        builder.addCase(createDeviceReadingExtraField.rejected, (state, action) => {
            state.loadingCreateDeviceReadingExtraField = false;
            state.createDeviceReadingExtraField = action.payload;
            state.createCommentSuccess = false;
            state.createCommentError = action.payload?.message || 'Error saving comment';
        });
    },
});

export const { actions, reducer } = driverPerformanceEditEventVideoRedux;
export const { reset, openModalEditEvent, resetCreateDeviceReadingExtraField, updateEventExtraFieldsLocally } = actions;
export default reducer;
